import React, { useState, useEffect, useCallback } from "react";
import { format } from 'date-fns';
import {
  Box, Typography, ToggleButtonGroup, ToggleButton,
  IconButton, Tooltip, useTheme, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { LineChart } from '@mui/x-charts/LineChart';
import { getEnv } from "../helpers/env";
import Loading from "../components/Loading";

const AGENTS_URL =
  getEnv("REACT_APP_AGENTS_URL") ||
  "https://clue2app-agents.control.apps.clue2.app";

const TIME_RANGES = [
  { id: "1h", label: "1H", hours: 1 },
  { id: "6h", label: "6H", hours: 6 },
  { id: "12h", label: "12H", hours: 12 },
  { id: "24h", label: "24H", hours: 24 },
];

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(3, Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i >= 2 ? 1 : 0)} ${units[i]}`;
}

function formatMB(bytes) {
  if (!bytes) return 0;
  return bytes / (1024 * 1024);
}

const MetricsPanel = (props) => {
  const { service } = props;
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Resolve the service name to filter by
  const serviceName = service?.metadata?.name || service?.displayName || service?.name || "";
  // Clean: strip timestamp prefix and -image suffix for matching
  const cleanName = serviceName.replace(/^\d+-/, "").replace(/-image$/, "");

  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("1h");
  const [metricsData, setMetricsData] = useState(null);
  const [error, setError] = useState(null);

  const fetchMetrics = useCallback(async () => {
    const range = TIME_RANGES.find(t => t.id === timeRange) || TIME_RANGES[0];
    try {
      const response = await fetch(`${AGENTS_URL}/costs/metrics-history?hours=${range.hours}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setMetricsData(data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch metrics:", err);
      setError(err.message);
    }
  }, [timeRange]);

  useEffect(() => {
    setIsLoading(true);
    fetchMetrics().finally(() => setIsLoading(false));
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  // Filter data for this specific service
  const chartData = (metricsData?.data || []).map(point => {
    const svc = (point.services || []).find(s => {
      const n = (s.name || "").replace(/^\d+-/, "").replace(/-image$/, "");
      return n === cleanName || s.name === serviceName;
    });
    return {
      timestamp: new Date(point.timestamp).getTime(),
      cpu: svc ? svc.cpu * 1000 : 0,
      memoryMB: svc ? formatMB(svc.memory) : 0,
      ccu: svc ? svc.ccu : 0,
      pods: svc ? svc.pods : 0,
    };
  });

  const latestPoint = chartData.length > 0 ? chartData[chartData.length - 1] : null;

  function timeFormatter(value) {
    if (!value) return "";
    const date = new Date(value);
    return format(date, 'HH:mm');
  }

  const chartSx = {
    '& .MuiChartsAxis-label': { fill: isDark ? '#94a3b8' : '#64748b', fontSize: 11 },
    '& .MuiChartsAxis-tickLabel': { fill: isDark ? '#94a3b8' : '#64748b', fontSize: 10 },
    '& .MuiChartsGrid-line': { stroke: isDark ? '#1e293b' : '#f1f5f9' },
    '& .MuiAreaElement-root': { fillOpacity: 0.08 },
    '& .MuiLineElement-root': { strokeWidth: 2 },
    '& .MuiMarkElement-root': { display: 'none' },
  };

  const panelSx = {
    p: 1.5, borderRadius: 2, mb: 1.5,
    bgcolor: isDark ? 'background.paper' : '#fff',
    border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
          {cleanName}
        </Typography>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(_, val) => val && setTimeRange(val)}
          size="small"
          sx={{ mr: 1 }}
        >
          {TIME_RANGES.map(r => (
            <ToggleButton key={r.id} value={r.id} sx={{ px: 1, py: 0.3, fontSize: '0.7rem' }}>
              {r.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => { setIsLoading(true); fetchMetrics().finally(() => setIsLoading(false)); }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {isLoading ? (
        <Loading />
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error" variant="body2">Failed to load metrics: {error}</Typography>
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" variant="body2">No metrics data available yet.</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Metrics are collected every 5 minutes.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Current stats */}
          {latestPoint && (
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'CPU', value: `${latestPoint.cpu.toFixed(0)}m`, color: '#3b82f6' },
                { label: 'Memory', value: `${latestPoint.memoryMB.toFixed(0)} MB`, color: '#8b5cf6' },
                { label: 'CCU', value: latestPoint.ccu.toFixed(3), color: '#06b6d4' },
                { label: 'Pods', value: latestPoint.pods, color: '#10b981' },
              ].map(stat => (
                <Box key={stat.label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: stat.color }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* CPU + Memory side by side */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>
            <Box sx={panelSx}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} sx={{ fontSize: '0.65rem' }}>
                CPU Usage
              </Typography>
              <LineChart
                xAxis={[{ dataKey: 'timestamp', scaleType: 'time', valueFormatter: timeFormatter }]}
                yAxis={[{ label: 'millicores', min: 0 }]}
                series={[{
                  dataKey: 'cpu',
                  color: '#3b82f6',
                  area: true,
                  showMark: false,
                  valueFormatter: (v) => `${v?.toFixed(1)}m`,
                }]}
                dataset={chartData}
                height={180}
                grid={{ horizontal: true }}
                slotProps={{ legend: { hidden: true } }}
                sx={chartSx}
              />
            </Box>

            <Box sx={panelSx}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} sx={{ fontSize: '0.65rem' }}>
                Memory Usage
              </Typography>
              <LineChart
                xAxis={[{ dataKey: 'timestamp', scaleType: 'time', valueFormatter: timeFormatter }]}
                yAxis={[{ label: 'MB', min: 0 }]}
                series={[{
                  dataKey: 'memoryMB',
                  color: '#8b5cf6',
                  area: true,
                  showMark: false,
                  valueFormatter: (v) => `${v?.toFixed(0)} MB`,
                }]}
                dataset={chartData}
                height={180}
                grid={{ horizontal: true }}
                slotProps={{ legend: { hidden: true } }}
                sx={chartSx}
              />
            </Box>
          </Box>

          {/* CCU */}
          <Box sx={panelSx}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} sx={{ fontSize: '0.65rem' }}>
              CCU (Cluster Compute Units)
            </Typography>
            <LineChart
              xAxis={[{ dataKey: 'timestamp', scaleType: 'time', valueFormatter: timeFormatter }]}
              yAxis={[{ min: 0 }]}
              series={[{
                dataKey: 'ccu',
                color: '#06b6d4',
                area: true,
                showMark: false,
                valueFormatter: (v) => `${v?.toFixed(3)} CCU`,
              }]}
              dataset={chartData}
              height={160}
              grid={{ horizontal: true }}
              slotProps={{ legend: { hidden: true } }}
              sx={chartSx}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default MetricsPanel;
